import React, { useMemo } from 'react';
import {
  StudentProfile,
  isStudentListRecencyActive,
  isStudentDeletableByAcademy,
  STUDENT_DELETE_BLOCKED_TOOLTIP,
} from '@court-sync/shared';
import {
  PaginatedTable,
  type PaginatedTableColumn,
  type PaginationState,
} from '@/components/ui/PaginatedTable';
import { Button, Badge, Tooltip } from '@/components/ui';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { UserAvatar } from '@/components/UserAvatar';
import { Info, MoreVertical, Mail, GraduationCap, Trash2 } from 'lucide-react';

/** Staff-added / CSV rows (`is_manual`). Invite email means someone can still complete online signup; without it they may be roster-only (e.g. adults with no guardian). */
function manualRosterBadge(s: StudentProfile): { label: string; className: string; title: string } | null {
  if (!s.isManual) return null;
  const invite = s.inviteEmail?.trim();
  if (invite) {
    return {
      label: 'Awaiting signup',
      className:
        'rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-900 dark:bg-amber-900/40 dark:text-amber-100',
      title:
        'Invite email on file. When that person registers or signs in for this academy using the same email, this record links to their account.',
    };
  }
  return {
    label: 'Roster only',
    className:
      'rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-700 dark:bg-slate-800 dark:text-slate-300',
    title:
      'No invite email — academy roster entry only. Use an invite email if someone should complete online signup (e.g. a parent for a junior, or the student for adults).',
  };
}

function ProgramGroupCell({ student }: { student: StudentProfile }) {
  const m = student.enrollmentListMeta;
  if (!m?.programName && !m?.ageOrPathwayLabel) {
    return <span className="text-xs text-muted-foreground">No enrolment</span>;
  }
  const color = m.calendarColor;
  const showDot = Boolean(color);
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      {m.programName ? (
        <span className="truncate text-xs font-medium text-foreground">{m.programName}</span>
      ) : null}
      {m.ageOrPathwayLabel ? (
        <span className="inline-flex min-w-0 items-center gap-1.5 text-xs sm:text-sm text-muted-foreground">
          {showDot ? (
            <span
              className="inline-block h-2.5 w-2.5 shrink-0 rounded-full border border-black/10"
              style={{ backgroundColor: color }}
              title="Age group colour"
              aria-hidden
            />
          ) : null}
          <span className="truncate">{m.ageOrPathwayLabel}</span>
        </span>
      ) : null}
    </div>
  );
}

function calcAge(dob: Date | undefined | null): number | null {
  if (!dob) return null;
  const t = new Date();
  const b = new Date(dob);
  let a = t.getFullYear() - b.getFullYear();
  const m = t.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && t.getDate() < b.getDate())) a--;
  return a;
}

function DeleteStudentDropdownItem({
  student,
  onDeleteStudent,
}: {
  student: StudentProfile;
  onDeleteStudent: (student: StudentProfile) => void;
}) {
  const deletable = isStudentDeletableByAcademy(student);
  const item = (
    <DropdownMenuItem
      disabled={!deletable}
      onClick={() => {
        if (deletable) onDeleteStudent(student);
      }}
      className="flex items-center gap-2 text-destructive focus:text-destructive"
    >
      <Trash2 className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
      Delete student
    </DropdownMenuItem>
  );

  if (deletable) {
    return item;
  }

  return (
    <Tooltip content={STUDENT_DELETE_BLOCKED_TOOLTIP}>
      <div>{item}</div>
    </Tooltip>
  );
}

interface StudentProfileMobileCardProps {
  student: StudentProfile;
  ownerBulk: boolean;
  selected: boolean;
  onToggleSelect?: (id: string) => void;
  onRowNavigate?: (student: StudentProfile) => void;
  onViewDetails?: (student: StudentProfile) => void;
  onSendGuardianInvite?: (student: StudentProfile) => void;
  onAddToLesson?: (student: StudentProfile) => void;
  onDeleteStudent?: (student: StudentProfile) => void;
  showActions: boolean;
}

function StudentProfileMobileCard({
  student,
  ownerBulk,
  selected,
  onToggleSelect,
  onRowNavigate,
  onViewDetails,
  onSendGuardianInvite,
  onAddToLesson,
  onDeleteStudent,
  showActions,
}: StudentProfileMobileCardProps) {
  const rosterBadge = manualRosterBadge(student);
  const age = calcAge(student.dateOfBirth);
  const recencyActive = isStudentListRecencyActive(student.listRecencyDate);
  const clickable = Boolean(onRowNavigate);

  const inner = (
    <>
      <div className="flex items-start gap-2.5">
        {ownerBulk && onToggleSelect ? (
          <input
            type="checkbox"
            checked={selected}
            onChange={() => onToggleSelect(student.id)}
            aria-label={`Select ${student.firstName} ${student.lastName}`}
            className="mt-1 shrink-0 rounded border-gray-300"
            onClick={(e) => e.stopPropagation()}
          />
        ) : null}
        <UserAvatar firstName={student.firstName} lastName={student.lastName} size="sm" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="truncate font-medium text-gray-900 dark:text-white">
              {student.firstName} {student.lastName}
            </span>
            {rosterBadge ? (
              <span className={rosterBadge.className} title={rosterBadge.title}>
                {rosterBadge.label}
              </span>
            ) : null}
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {age != null ? `${age} yrs` : '—'} · <span className="capitalize">{student.gender}</span>
            {' · '}
            <span className="capitalize">{student.skillLevel || '—'}</span>
          </p>
        </div>
        <Badge variant={recencyActive ? 'success' : 'secondary'} className="shrink-0 text-xs">
          {recencyActive ? 'Active' : 'Inactive'}
        </Badge>
      </div>

      <div className="mt-2.5 grid grid-cols-2 gap-x-3 gap-y-2 border-t border-border/60 pt-2.5 text-xs">
        <div className="min-w-0">
          <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Program</p>
          <div className="mt-0.5">
            <ProgramGroupCell student={student} />
          </div>
        </div>
        <div>
          <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Attendance</p>
          <p className="mt-0.5 tabular-nums text-foreground">
            {student.attendanceSummary?.attendanceRate != null ? (
              `${student.attendanceSummary.attendanceRate}%`
            ) : (
              <span className="text-muted-foreground">Not tracked</span>
            )}
          </p>
        </div>
      </div>

      {showActions && onViewDetails && onSendGuardianInvite ? (
        <div
          className="mt-2.5 flex justify-end border-t border-border/60 pt-2"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 gap-1.5 px-2.5"
                aria-label={`Actions for ${student.firstName} ${student.lastName}`}
              >
                <MoreVertical className="h-4 w-4" aria-hidden />
                Actions
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => onViewDetails(student)}>View details</DropdownMenuItem>
              {onAddToLesson ? (
                <DropdownMenuItem
                  onClick={() => onAddToLesson(student)}
                  className="flex items-center gap-2"
                >
                  <GraduationCap className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
                  Add to lesson
                </DropdownMenuItem>
              ) : null}
              <Tooltip
                content={
                  student.inviteEmail?.trim()
                    ? 'Send an email with a link to the academy register form. The link includes a secure token — guardians can use any email when signing up if they open that link. Matching the invite email still works as a fallback.'
                    : 'Add an invite email on the student first if they should complete online signup.'
                }
              >
                <div>
                  <DropdownMenuItem
                    disabled={!student.inviteEmail?.trim()}
                    onClick={() => onSendGuardianInvite(student)}
                    className="flex items-center gap-2"
                  >
                    <Mail className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
                    Send signup invite
                  </DropdownMenuItem>
                </div>
              </Tooltip>
              {onDeleteStudent ? (
                <DeleteStudentDropdownItem student={student} onDeleteStudent={onDeleteStudent} />
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ) : null}
    </>
  );

  const className =
    'w-full rounded-lg border border-border bg-card p-3 text-left text-sm shadow-sm transition-colors';

  if (clickable && onRowNavigate) {
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={() => onRowNavigate(student)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onRowNavigate(student);
          }
        }}
        className={`${className} cursor-pointer hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring`}
      >
        {inner}
      </div>
    );
  }

  return <div className={className}>{inner}</div>;
}

export interface AcademyStudentProfilesTableProps {
  students: StudentProfile[];
  pagination: PaginationState | null;
  loading?: boolean;
  emptyMessage?: string;
  onPageChange: (page: number) => void;
  variant: 'owner' | 'coach';
  /** Owner bulk selection */
  selectedIds?: Set<string>;
  onToggleSelect?: (id: string) => void;
  onToggleSelectAll?: () => void;
  /** Coach (and optional keyboard): navigate when row activated */
  onRowNavigate?: (student: StudentProfile) => void;
  /** Owner: student management detail route */
  onViewDetails?: (student: StudentProfile) => void;
  onSendGuardianInvite?: (student: StudentProfile) => void;
  onAddToLesson?: (student: StudentProfile) => void;
  onDeleteStudent?: (student: StudentProfile) => void;
  className?: string;
}

/**
 * Paginated student roster table shared by academy owner Student Management and coach Students.
 */
export function AcademyStudentProfilesTable({
  students,
  pagination,
  loading = false,
  emptyMessage = 'No students found.',
  onPageChange,
  variant,
  selectedIds = new Set(),
  onToggleSelect,
  onToggleSelectAll,
  onRowNavigate,
  onViewDetails,
  onSendGuardianInvite,
  onAddToLesson,
  onDeleteStudent,
  className = 'overflow-x-auto',
}: AcademyStudentProfilesTableProps) {
  const ownerBulk = variant === 'owner' && onToggleSelect && onToggleSelectAll;
  const showOwnerActions = variant === 'owner' && Boolean(onViewDetails && onSendGuardianInvite);

  const columns: PaginatedTableColumn<StudentProfile>[] = useMemo(() => {
    const cols: PaginatedTableColumn<StudentProfile>[] = [];

    if (ownerBulk) {
      cols.push({
        key: 'select',
        header: (
          <div className="flex justify-center">
            <input
              type="checkbox"
              aria-label="Select all on this page"
              checked={students.length > 0 && selectedIds.size === students.length}
              onChange={onToggleSelectAll}
              className="rounded border-gray-300"
            />
          </div>
        ),
        className: 'w-10 align-middle',
        render: (s) => (
          <div className="flex justify-center">
            <input
              type="checkbox"
              checked={selectedIds.has(s.id)}
              onChange={() => onToggleSelect!(s.id)}
              aria-label={`Select ${s.firstName} ${s.lastName}`}
              className="rounded border-gray-300"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        ),
      });
    }

    cols.push(
      {
        key: 'name',
        header: 'Student',
        render: (s) => (
          <div className="flex items-center gap-2">
            <UserAvatar firstName={s.firstName} lastName={s.lastName} size="sm" />
            <span className="font-medium text-gray-900 dark:text-white">
              {s.firstName} {s.lastName}
            </span>
            {(() => {
              const b = manualRosterBadge(s);
              return b ? (
                <span className={b.className} title={b.title}>
                  {b.label}
                </span>
              ) : null;
            })()}
          </div>
        ),
      },
      {
        key: 'age',
        header: 'Age / Gender',
        render: (s) => {
          const age = calcAge(s.dateOfBirth);
          return (
            <span>
              {age != null ? `${age} / ` : '— / '}
              {s.gender}
            </span>
          );
        },
      },
      {
        key: 'skill',
        header: 'Skill',
        render: (s) => <span className="capitalize">{s.skillLevel || '—'}</span>,
      },
      {
        key: 'path',
        header: 'Program / group',
        render: (s) => <ProgramGroupCell student={s} />,
      },
      {
        key: 'att',
        header: 'Attendance',
        render: (s) =>
          s.attendanceSummary?.attendanceRate != null ? (
            <span className="tabular-nums text-foreground">{s.attendanceSummary.attendanceRate}%</span>
          ) : (
            <span className="text-xs text-muted-foreground">Not tracked</span>
          ),
      },
      {
        key: 'status',
        header: (
          <span className="inline-flex items-center gap-1.5">
            Status
            <Tooltip
              content={
                'Active: had a lesson booking (created or updated) or an attended session in the last 3 months. Inactive: neither in that window.'
              }
            >
              <button
                type="button"
                className="inline-flex cursor-default text-muted-foreground hover:text-foreground rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label="How Status is determined"
              >
                <Info className="h-3.5 w-3.5" />
              </button>
            </Tooltip>
          </span>
        ),
        render: (s) => {
          const recencyActive = isStudentListRecencyActive(s.listRecencyDate);
          return (
            <Badge variant={recencyActive ? 'success' : 'secondary'}>
              {recencyActive ? 'Active' : 'Inactive'}
            </Badge>
          );
        },
      }
    );

    if (variant === 'owner' && onViewDetails && onSendGuardianInvite) {
      cols.push({
        key: 'actions',
        header: '',
        className: 'w-14 text-right',
        render: (s) => (
          <div onClick={(e) => e.stopPropagation()}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0"
                  aria-label={`Actions for ${s.firstName} ${s.lastName}`}
                >
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={() => onViewDetails(s)}>View details</DropdownMenuItem>
                {onAddToLesson ? (
                  <DropdownMenuItem
                    onClick={() => onAddToLesson(s)}
                    className="flex items-center gap-2"
                  >
                    <GraduationCap className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
                    Add to lesson
                  </DropdownMenuItem>
                ) : null}
                <Tooltip
                  content={
                    s.inviteEmail?.trim()
                      ? 'Send an email with a link to the academy register form. The link includes a secure token — guardians can use any email when signing up if they open that link. Matching the invite email still works as a fallback.'
                      : 'Add an invite email on the student first if they should complete online signup.'
                  }
                >
                  <div>
                    <DropdownMenuItem
                      disabled={!s.inviteEmail?.trim()}
                      onClick={() => onSendGuardianInvite(s)}
                      className="flex items-center gap-2"
                    >
                      <Mail className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
                      Send signup invite
                    </DropdownMenuItem>
                  </div>
                </Tooltip>
                {onDeleteStudent ? (
                  <DeleteStudentDropdownItem student={s} onDeleteStudent={onDeleteStudent} />
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ),
      });
    }

    return cols;
  }, [
    variant,
    ownerBulk,
    students,
    selectedIds,
    onToggleSelect,
    onToggleSelectAll,
    onViewDetails,
    onSendGuardianInvite,
    onAddToLesson,
    onDeleteStudent,
  ]);

  return (
    <PaginatedTable<StudentProfile>
      columns={columns}
      data={students}
      pagination={pagination}
      loading={loading}
      emptyMessage={emptyMessage}
      onPageChange={onPageChange}
      className={className}
      noWrapperPadding
      onRowClick={variant === 'coach' ? onRowNavigate : undefined}
      renderMobileCard={(student) => (
        <StudentProfileMobileCard
          student={student}
          ownerBulk={Boolean(ownerBulk)}
          selected={selectedIds.has(student.id)}
          onToggleSelect={onToggleSelect}
          onRowNavigate={variant === 'coach' ? onRowNavigate : undefined}
          onViewDetails={onViewDetails}
          onSendGuardianInvite={onSendGuardianInvite}
          onAddToLesson={onAddToLesson}
          onDeleteStudent={onDeleteStudent}
          showActions={showOwnerActions}
        />
      )}
    />
  );
}
